import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api/axiosInstance";
import { logoutUser } from "./authSlice";

const getErrorMessage = (err, fallback) =>
  err.response?.data?.message || fallback;

// --- Thunks -----------------------------------------------------------

// filters is optional: { startDate, endDate, addedBy, technology, clientId,
// page, limit }. Any key that's missing/empty is simply left off the query
// string — page/limit specifically being absent means "no pagination,
// give me everything" (see backend/utils/pagination.js) — several
// existing callers (NewOptimization's candidate picker) rely on that.
export const fetchCandidates = createAsyncThunk(
  "candidates/fetchCandidates",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      for (const key of ["startDate", "endDate", "addedBy", "technology", "clientId", "page", "limit"]) {
        if (filters[key]) params[key] = filters[key];
      }
      const res = await api.get("/candidate", { params });
      return { candidates: res.data.candidates, pagination: res.data.pagination || null };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to load candidates."));
    }
  }
);

export const fetchCandidateById = createAsyncThunk(
  "candidates/fetchCandidateById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/candidate/${id}`);
      // resumeHistory is newest-first; the entry with isLatestMaster: true
      // is the current master resume.
      return { candidate: res.data.candidate, resumeHistory: res.data.resumeHistory };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to load candidate."));
    }
  }
);

export const createCandidate = createAsyncThunk(
  "candidates/createCandidate",
  async (formData, { rejectWithValue }) => {
    try {
      // formData is always a FormData instance (built in CandidateForm) so a
      // resume file can ride along — axios sets the multipart boundary
      // header automatically when the body is FormData.
      const res = await api.post("/candidate", formData);
      const { candidate, masterResume } = res.data;

      // GET /candidate (fetchCandidates) annotates every row with
      // latestMasterResume so the list can show it without a second round
      // trip. POST /candidate returns the same resume under a different key
      // (masterResume) instead — without this, the row this thunk adds to
      // state.candidates lacks latestMasterResume and the table shows "Not
      // uploaded" until the next full fetchCandidates (e.g. a page refresh)
      // recomputes it. Build the same shape here so the new row is correct
      // immediately.
      return {
        ...candidate,
        latestMasterResume: masterResume
          ? {
              id: masterResume._id,
              fileName: masterResume.fileName,
              version: masterResume.version,
              uploadedAt: masterResume.createdAt,
            }
          : null,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to create candidate."));
    }
  }
);

export const updateCandidate = createAsyncThunk(
  "candidates/updateCandidate",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/candidate/${id}`, formData);
      return res.data.candidate;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to update candidate."));
    }
  }
);

export const deleteCandidate = createAsyncThunk(
  "candidates/deleteCandidate",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/candidate/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to delete candidate."));
    }
  }
);

// Uploads a NEW master resume version — never overwrites. Field name must
// be "resume" to match the backend's multer config.
export const uploadMasterResume = createAsyncThunk(
  "candidates/uploadMasterResume",
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("resume", file);
      const res = await api.post(`/candidate/${id}/resume`, formData);
      return res.data.masterResume;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to upload resume."));
    }
  }
);

// LLM Op #1 — Resume Parser. Runs Claude over a specific resume version's
// already-extracted text and stores the structured result (skills,
// per-company experience with bullets, etc.) on that version — needed
// before Experience Rewriter (Stage 4 Op #6) can run for this candidate.
export const parseResumeVersion = createAsyncThunk(
  "candidates/parseResumeVersion",
  async ({ candidateId, versionId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/candidate/${candidateId}/resume-versions/${versionId}/parse`);
      return { versionId, parsedData: res.data.parsedData };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to parse resume with AI."));
    }
  }
);

// Fills in blank Candidate fields (title, company, skills, experience,
// education, certs) from a resume version's parsedData — so the recruiter
// doesn't have to re-type what the LLM already extracted. Non-destructive
// by default (only fills empty fields); overwrite: true replaces everything.
export const applyParsedResume = createAsyncThunk(
  "candidates/applyParsedResume",
  async ({ id, versionId, overwrite = false, confirmMismatch = false }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/candidate/${id}/apply-parsed-resume`, { versionId, overwrite, confirmMismatch });
      return {
        candidate: res.data.candidate,
        needsConfirmation: !!res.data.needsConfirmation,
        nameMismatchWarning: res.data.nameMismatchWarning || null,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to apply parsed resume data."));
    }
  }
);

// --- Slice --------------------------------------------------------------

const initialState = {
  candidates: [],
  pagination: null,
  selectedCandidate: null,
  resumeHistory: [], // full version history for selectedCandidate, newest first
  status: "idle", // idle | loading | succeeded | failed
  error: null,
};

const candidateSlice = createSlice({
  name: "candidates",
  initialState,
  reducers: {
    clearCandidateError: (state) => {
      state.error = null;
    },
    clearSelectedCandidate: (state) => {
      state.selectedCandidate = null;
      state.resumeHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchCandidates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.candidates = action.payload.candidates;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Fetch one (+ resume history)
      .addCase(fetchCandidateById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCandidateById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedCandidate = action.payload.candidate;
        state.resumeHistory = action.payload.resumeHistory;
      })
      .addCase(fetchCandidateById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Create
      .addCase(createCandidate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createCandidate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.candidates.unshift(action.payload);
      })
      .addCase(createCandidate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update
      .addCase(updateCandidate.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateCandidate.fulfilled, (state, action) => {
        state.status = "succeeded";
        const idx = state.candidates.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.candidates[idx] = { ...state.candidates[idx], ...action.payload };
        state.selectedCandidate = action.payload;
      })
      .addCase(updateCandidate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.candidates = state.candidates.filter((c) => c._id !== action.payload);
      })
      .addCase(deleteCandidate.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload a new master resume version — prepend to history and clear
      // the isLatestMaster flag on every prior entry.
      .addCase(uploadMasterResume.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadMasterResume.fulfilled, (state, action) => {
        state.status = "succeeded";
        // action.payload here is the raw ResumeVersion document from
        // POST /candidate/:id/resume (_id, extractedText, ...) — a
        // different shape than the rows getCandidateById sends (id,
        // hasExtractedText, ...). Spreading it in as-is used to leave the
        // new row without `id`/`hasExtractedText`/`parsedData`, so
        // Download and Parse with AI silently broke on a just-uploaded
        // version until a refetch replaced it with the correct shape.
        const mr = action.payload;
        state.resumeHistory = state.resumeHistory.map((rv) => ({ ...rv, isLatestMaster: false }));
        state.resumeHistory.unshift({
          id: mr._id,
          optimizationId: mr.optimizationId ?? null,
          version: mr.version,
          type: mr.type,
          fileName: mr.fileName,
          atsScore: mr.atsScore ?? null,
          createdBy: mr.createdBy,
          createdAt: mr.createdAt,
          isLatestMaster: true,
          hasExtractedText: !!mr.extractedText,
          parsedData: mr.parsedData || null,
        });
      })
      .addCase(uploadMasterResume.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // AI-parse one resume version — patch parsedData into resumeHistory
      // in place so the UI updates without a full refetch.
      // Parsing has its own per-button spinner (useParseAndFill), so it
      // deliberately does NOT touch state.status — otherwise the page-level
      // "loading" flag flips and unrelated buttons (Upload, Save) start
      // showing "Uploading..." / "Saving..." while the AI is running.
      .addCase(parseResumeVersion.pending, (state) => {
        state.error = null;
      })
      .addCase(parseResumeVersion.fulfilled, (state, action) => {
        // resumeHistory rows are shaped by getCandidateById as { id, ... }
        // (not _id) — matching on rv._id here never found anything, so the
        // row never got its parsedData patched in and the button stayed on
        // "Parse with AI" until a refetch (e.g. a page refresh) pulled the
        // already-saved parsedData from the server.
        const idx = state.resumeHistory.findIndex((rv) => rv.id === action.payload.versionId);
        if (idx !== -1) state.resumeHistory[idx].parsedData = action.payload.parsedData;
      })
      .addCase(parseResumeVersion.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(applyParsedResume.fulfilled, (state, action) => {
        state.selectedCandidate = action.payload.candidate;
        const idx = state.candidates.findIndex((c) => c._id === action.payload.candidate._id);
        if (idx !== -1) state.candidates[idx] = { ...state.candidates[idx], ...action.payload.candidate };
      })
      .addCase(applyParsedResume.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Wipe everything on logout — prevents a previously-logged-in
      // account's candidates from ever being visible or clickable under
      // a different account's session, even for a moment.
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { clearCandidateError, clearSelectedCandidate } = candidateSlice.actions;
export default candidateSlice.reducer;